import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchAPI } from "../../utils/api";
import {
    Box, CircularProgress, Container, IconButton, Tooltip, Typography,
    MenuItem, Paper, Divider, Avatar, Alert, Stack, Snackbar, Chip
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessIcon from '@mui/icons-material/Business';
import TextField from "@mui/material/TextField";
import { Currency, ProducerDashboard } from "../interfaces/ProducerDashboard";
import ProducerDashboardContent from "./ProducerDashboardContent";

export default function ProducerDashboardTab() {
    const [searchParams] = useSearchParams();

    const [producerUid, setProducerUid] = useState('');
    const [currency, setCurrency] = useState<Currency>();

    const [inputUid, setInputUid] = useState('');
    const [inputCurrency, setInputCurrency] = useState<Currency>();

    const institutionId = 'LEMONWAY';
    const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>([]);
    const [dashboardData, setDashboardData] = useState<ProducerDashboard | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info' as 'info' | 'error' | 'success'
    });

    // GET CURRENCIES
    useEffect(() => {
        fetchAPI('/operators/currency')
            .then(res => res.ok ? res.json() : [])
            .then((data: Currency[]) => {
                const codes = data.filter(code => code !== ('ALL' as any));
                setAvailableCurrencies(codes.length > 0 ? codes : []);
            });
    }, []);

    // GET DASHBOARD
    const getDashboard = useCallback(async (uid: string, ccy: Currency, isManualRefresh = false) => {
        if (!uid || !ccy || !institutionId) return;

        setIsLoading(true);
        const url = `/producers/dashboard/${institutionId}/producer/${uid}/currency/${ccy}${isManualRefresh ? '?forceRefresh=true' : ''}`;

        try {
            const res = await fetchAPI(url);
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
            setProducerUid(uid);
            setCurrency(ccy);
            setDashboardData(data);

            if (isManualRefresh) {
                setSnackbar({ open: true, message: 'Data refreshed from source', severity: 'success' });
            }
        } catch (err: any) {
            setDashboardData(null);
            setSnackbar({ open: true, message: err.message || 'Server connection failed', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [institutionId]);

    // Auto-load with URL
    useEffect(() => {
        const urlId = searchParams.get('producerId');
        const urlCcy = searchParams.get('currency') as Currency;

        if (urlId && urlCcy) {
            setInputUid(urlId);
            setInputCurrency(urlCcy);
            getDashboard(urlId, urlCcy, false);
        }
    }, [searchParams, getDashboard]);

    // UPDATE PRODUCER STATUS
    const handleSyncStatus = async () => {
        if (!dashboardData || !producerUid || !currency) return;
        const associatedOps = dashboardData.associated_operators || [];
        const firstOp = associatedOps.length > 0 ? associatedOps[0] : institutionId;
        const targetStatus = dashboardData.institution_status;

        setIsUpdating(true);
        try {
            const res = await fetchAPI(`/producers/${producerUid}/operators/${firstOp}`, {
                method: 'PATCH',
                body: JSON.stringify({ status: targetStatus.toUpperCase() })
            });
            if (!res.ok) throw new Error("Sync failed");
            setDashboardData(prev => prev ? { ...prev, one_comm_status: targetStatus } : null);
            setSnackbar({ open: true, message: `Status successfully synced`, severity: 'success' });
        } catch (err: any) {
            setSnackbar({ open: true, message: err.message, severity: 'error' });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 5, bgcolor: '#fdfdfd', minHeight: '600px', position: 'relative' }}>

                {/* HEADER & SEARCH BAR */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, gap: 2, alignItems: 'flex-start' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50 }}><BusinessIcon /></Avatar>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800 }}>Producer Dashboard</Typography>
                            {dashboardData && (
                                <>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ bgcolor: '#e3f2fd', color: 'primary.dark', px: 1.5, py: 0.4, borderRadius: 1, fontWeight: 800, fontSize: '0.7rem' }}
                                        >
                                            {dashboardData.company_name}
                                        </Typography>
                                        <Chip
                                            label={dashboardData.one_comm_status}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800 }}
                                        />
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ bgcolor: '#e3f2fd', color: 'primary.dark', px: 1.5, py: 0.4, borderRadius: 1, fontWeight: 800, fontSize: '0.7rem' }}
                                        >
                                            ID: {producerUid}
                                        </Typography>
                                    </Stack>
                                </>
                            )}
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ bgcolor: '#fff', p: 1, borderRadius: 2, border: '1px solid #eee', alignItems: 'center' }}>
                        <TextField size="small" label="Inst." value={institutionId} disabled sx={{ width: 100 }} />
                        <TextField size="small" label="UID" value={inputUid} onChange={e => setInputUid(e.target.value)} sx={{ width: 150 }} />
                        <TextField size="small" select label="CCY" value={inputCurrency || ''} onChange={e => setInputCurrency(e.target.value as Currency)} sx={{ width: 80 }}>
                            {availableCurrencies.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                        <Tooltip title="Search">
                            <span>
                                <IconButton
                                    onClick={() => getDashboard(inputUid, inputCurrency!, false)}
                                    color="primary"
                                    disabled={isLoading || !inputUid || !inputCurrency}
                                >
                                    <SearchIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {dashboardData && (
                            <Tooltip title="Refresh data">
                                <IconButton onClick={() => getDashboard(producerUid, currency!, true)} color="secondary" disabled={isLoading}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Box>

                <Divider sx={{ mb: 4 }} />

                {/* CONTENT AREA */}
                {isLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2 }}>
                        <CircularProgress size={50} />
                        <Typography variant="h6" color="text.secondary">Loading Dashboard...</Typography>
                    </Box>
                ) : dashboardData && currency ? (
                    <ProducerDashboardContent
                        currency={currency}
                        dashboardData={dashboardData}
                        handleSyncStatus={handleSyncStatus}
                        institutionId={institutionId}
                        isUpdating={isUpdating}
                        producerUid={producerUid}
                    />
                ) : (
                    <Box sx={{ py: 10, textAlign: 'center', color: 'text.disabled' }}>
                        <Typography variant="h6">Please search for a producer to display data</Typography>
                    </Box>
                )}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={5000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Paper>
        </Container>
    );
}